/**
 * @file picking-item-texts.ts
 * @description Реестр подписей состава задания на сборку (phase19.23–19.24).
 *
 * Тексты «почему строк нет» и «позиция не привязана к каталогу» живут рядом:
 * список позиций и полоса-предупреждение обязаны говорить об одной и той же
 * ситуации одинаково. Новый повод = запись в реестре, а не ветка в разметке.
 */

import type { PickingUnlinkedItemDto } from "../../../lib/api-picking";

/** Подписи пустого задания — данными, не каскадом `if`. */
export const EMPTY_TEXT = {
  services_only:
    "В задании нет складских позиций (услуги или доставка). Проведите сборку — заказ перейдёт в «Собран».",
  // Пересборка доступна — зовём нажать кнопку рядом, а не искать пункт в меню.
  empty_rebuildable:
    "Строки задания потерялись. Пересоберите задание — состав возьмётся из заказа заново.",
  // Пересобрать нельзя (задание в работе и строки есть) — говорим, что делать.
  empty_locked:
    "Строки задания потерялись. Чтобы пересобрать состав, сначала верните задание в очередь.",
  // OQ-50: чинится правкой ЗАКАЗА, пересборка задания тут бессильна.
  unlinked:
    "В заказе есть позиция без товара каталога — её нельзя собрать. Исправьте заказ, задание пересоберётся автоматически.",
} as const;

/**
 * Почему позиции нельзя отмечать. Считается в `usePickingTaskScreen` рядом с
 * `editable`: компонент про подачу, а не про права.
 */
export type PickingLockReason = "closed" | "foreign" | "noPermission";

/**
 * Подсказка под полем скана у НЕредактируемого задания — реестром, не ветками.
 * Раньше во всех трёх случаях писали «Задание закрыто», и кладовщик у живого
 * чужого задания читал неправду (находка ui-visual-qa phase19.24 этап 0).
 * Вторая половина фразы одинаковая намеренно: меняется только причина, а что
 * поле всё-таки делает — фильтрует — человек читает на том же месте.
 */
export const PICKING_LOCK_HINT: Record<PickingLockReason, string> = {
  closed: "Задание закрыто: поле фильтрует список позиций.",
  foreign: "Задание взял другой сотрудник: поле только фильтрует список.",
  noPermission:
    "Отмечать позиции может только кладовщик: поле только фильтрует список.",
};

/**
 * Полоса-предупреждение о непривязанных позициях: перечисляем названия, чтобы
 * менеджер знал, какую строку заказа чинить, и говорим о блокировке прямо.
 */
export function formatUnlinkedNotice(
  items: readonly PickingUnlinkedItemDto[],
): string {
  const names = items.map((i) => `«${i.name}»`).join(", ");
  const subject =
    items.length === 1 ? `Позиция ${names} не привязана` : `Позиции ${names} не привязаны`;
  return `${subject} к товару каталога — исправьте заказ. Проведение недоступно.`;
}
